import { Component, createEffect, For } from "solid-js";
import { CampaignTile } from "@/types/checkout";
import { useCheckoutFlow } from "@/contexts/checkout-flow/checkout-flow.provider";
import { useCampaignSelection } from "@/hooks/useCampaignSelection";
import CampaignTileButton from "../campaign-tile-button/CampaignTileButton";
import { publishCampaignTileClick } from "@/services/analytics.service";

interface CampaignSelectionProps {
  tiles: CampaignTile[];
  maxSelections: number;
}

const CampaignSelection: Component<CampaignSelectionProps> = (props) => {
  const { selections, setSelections } = useCheckoutFlow();
  const { selectedIds, isSelected, canSelectMore, toggleSelection } =
    useCampaignSelection(() => props.maxSelections);

  createEffect(() => {
    // Restore previous selections when returning to this screen
    if (selectedIds().size > 0 || selections().length === 0) return;

    const matchesPreviousSelection = (tile: CampaignTile) =>
      selections().some(
        (sel) => sel.month === tile.month && sel.year === tile.year,
      );

    props.tiles
      .filter(matchesPreviousSelection)
      .forEach((tile) => toggleSelection(tile.id, true));
  });

  createEffect(() => {
    // Sync selected tiles to context
    const selected = props.tiles
      .filter((t) => selectedIds().has(t.id))
      .map((t) => ({ month: t.month, year: t.year, price: t.price }));
    setSelections(selected);
  });

  const getTooltipText = (tile: CampaignTile): string | undefined => {
    if (tile.alreadyBooked) return "You have already selected this time slot";
    if (tile.available === false) return "This time slot is unavailable";
    if (!isSelected(tile.id) && !canSelectMore())
      return `You cannot select more than ${props.maxSelections} time slots`;
    return undefined;
  };

  const isDisabled = (tile: CampaignTile): boolean =>
    !!tile.alreadyBooked ||
    tile.available === false ||
    (!isSelected(tile.id) && !canSelectMore());

  const handleChange = (tile: CampaignTile, isNowSelected: boolean) => {
    if (tile.alreadyBooked || tile.available === false) return;
    if (isNowSelected && !canSelectMore()) return;
    publishCampaignTileClick(tile.month, tile.year);
    toggleSelection(tile.id, isNowSelected);
  };

  return (
    <div class="flex flex-col gap-FsSpacing1x">
      <div class="font-FsFontWeightSemibold text-FsFontSize0875x">
        Select up to {props.maxSelections} months
      </div>
      <fd-mfe-choice-button-group prop:columns={3}>
        <For each={props.tiles}>
          {(tile) => (
            <CampaignTileButton
              tile={tile}
              isSelected={isSelected(tile.id)}
              isDisabled={isDisabled(tile)}
              tooltipText={getTooltipText(tile)}
              onChange={(selected) => handleChange(tile, selected)}
            />
          )}
        </For>
      </fd-mfe-choice-button-group>
      <div class="text-FsFontSize0875x text-FsClrNeutralGray550">
        Note: Featured Dealer campaigns and time slots are subject to
        eligibility requirements. Time slots are also limited and typically
        available on a first come, first serve basis.
      </div>
    </div>
  );
};

export default CampaignSelection;
